import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { Label } from './Label';
import { Icon } from '../Icon';
import { IconDefinitions } from '../../../utils/types/icon-definitions';
import { InputWrapper, ErrorText } from './DefaultInput';

export interface FileUploadFile {
  id?: string;
  fileName: string;
  bytes: number;
  dataUrl: string;
  mimeType: string;
}

export interface FileUploadProps {
  id: string;
  label?: string;
  onChange: (files: { fileName: string, bytes: number, dataUrl: string, mimeType: string }[]) => void;
  placeholder?: string;
  type?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  multiFileUpload?: boolean;
  allowedFileTypes?: string[];
}

export const InputInnerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 6rem;
  border: 1px solid ${Colors.background};
  color: ${Colors.grayDark};
  background-color: ${Colors.background};
  padding: 2rem;
`;

export const StyledFileUpload = styled.input`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
`;

const StyledLabel = styled.label`
  z-index: 9999;
  border: 1px solid ${Colors.gray};
  min-height: 106px;
  height: 100%;
  width: 100%;
  color: ${Colors.gray};
  display: flex;
  font-size:3rem;
  background: ${Colors.background};
  outline: none;
  white-space: nowrap;
  -webkit-user-select: none;
  cursor: pointer;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
  }  
  
  &.local-error {
    border-color: ${Colors.error};
    padding-left: 1rem;
    color: ${Colors.error}; 
  }
  
  &:disabled {
    pointer-events: none;
    cursor: default;
  }
`;

const InnerTitle = styled.p`
  pointer-events: none;
  z-index: 1;
  text-align: left;
  width: 100%;
  display:block;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const DataFormat = styled.span`
  pointer-events: none;
  text-align: left;
  z-index: 1;
  width: 100%;
  display:block;
  margin-top: 1rem;
  font-size: .75rem;
`;

const FileSize = styled.span`
  text-align:left;
  display:block;
  width: 100%;
`;

const FilesList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 1rem;

  width: 100%;

  & > li {
  color: ${Colors.primary};
  position: relative;
  min-height: 120px;
  height: 100%;
  overflow: hidden;

  &.active > div {
    border-color: ${Colors.primary};
    color: ${Colors.grayDark};
  }
  
  &:hover {
    color: ${Colors.primaryLight};
    cursor: pointer;
   }  
  }
`;

const FileItem = styled.div`
  border: 1px solid ${Colors.gray};
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-wrap: anywhere;
  font-size: .75rem;
`;

const FileTypeIcon = styled(Icon)`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
`;

const FileName = styled.div`
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  margin: 1rem 0 0;
`;

const CloseIconWrapper = styled.div`
  position: absolute;
  right: .5rem;
  top: .5rem;
`;

const ExtendedErrorText = styled(ErrorText)`
  align-self:flex-start;
  padding-left: 0;
  `;

export const FileUpload: FC<FileUploadProps> = (props) => {
  const defaultAllowedFiles = ['application/pdf', 'image/png', 'image/jpg'];
  const {
    id,
    label = '',
    onChange,
    value,
    required = false,
    disabled = false,
    errorMessage = 'Bitte fügen Sie Dateien hinzu.',
    hasError = false,
    multiFileUpload = false,
    allowedFileTypes = defaultAllowedFiles,
  } = props;
  const [localError, setLocalError] = useState<boolean>(hasError);
  const [selectedFiles, setSelectedFiles] = useState<FileUploadFile[] | null>(null);

  useEffect(() => {
    setLocalError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (value && value.length > 0) {
      setLocalError(false);
    }
  }, [value]);

  useEffect(() => {
    if (selectedFiles && selectedFiles.length > 0) {
      onChange(selectedFiles);
    }
  }, [selectedFiles]);

  const handleLocalError = (files: FileUploadFile[]) => {
    if (required && (!files || files.length < 1)) {
      setLocalError(true);
    } else {
      setLocalError(false);
    }
  };

  const allowedFileTypesAsString = (fileTypes: string[]): string => {
    let result: string = '';

    fileTypes.map((type, index) => {

      if ((index + 1) < fileTypes.length) {
        result += `${type}, `;
      } else {
        result += type;
      }
    });

    return result;
  };

  function mimeTypeToIconName(extension: string): IconDefinitions {
    if (extension.includes('pdf')) return 'type-pdf';
    else if (extension.includes('image')) return 'type-image';
    return 'type-text';
  }

  return (
    <InputWrapper>
      {
        props.label &&
        <Label for={props.id}>{props.label}</Label>
      }
      <InputInnerWrapper>
        <InnerTitle>Dateianhänge hinzufügen</InnerTitle>
        <FilesList>
          {
            selectedFiles && selectedFiles.length > 0 && selectedFiles.map((file, index) => {
              return <li
                className={'active'}
                key={index}
                onClick={() => {
                  const updatedFiles = selectedFiles.filter((f, i) => i !== index);
                  setSelectedFiles(updatedFiles);
                  handleLocalError(updatedFiles);
                }}>
                <FileItem>
                  <CloseIconWrapper>
                    <Icon icon={'state-error'} size={16} />
                  </CloseIconWrapper>
                  <FileTypeIcon icon={mimeTypeToIconName(file.mimeType)} size={32} />
                  <FileName title={file.fileName}>{file.fileName}</FileName>
                  <FileSize>({parseInt((file.bytes / 1024 / 1024).toString())} MB)</FileSize>
                </FileItem>
              </li>;
            })
          }
          <li>
            {(multiFileUpload || (!selectedFiles || selectedFiles.length < 1)) && (
              <>
                <StyledLabel className={`${localError ? 'local-error' : ''}`}>+</StyledLabel>
                <StyledFileUpload
                accept={allowedFileTypesAsString(allowedFileTypes)}
                multiple={multiFileUpload}
                type={'file'}
                id={id}
                disabled={disabled}
                onBlur={() => handleLocalError(selectedFiles)}
                onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                  const filesList = event.target.files;
                  const files: FileUploadFile[] = await Promise.all(Object.keys(filesList).map((key) => new Promise<FileUploadFile>((resolve, reject) => {
                    let fileReader = new FileReader();

                    fileReader.onload = (e) => {
                    };
                    fileReader.onloadend = (e) => {

                      if (e.target.error) {
                        reject(null);
                      }

                      resolve({
                        fileName: filesList[key].name,
                        bytes: filesList[key].size,
                        mimeType: filesList[key].type,
                        dataUrl: e.target.result.toString(),
                      } as FileUploadFile);
                    };
                    fileReader.readAsDataURL(filesList[key]);
                  })));
                  const validFiles = files.filter((result) => result !== null);
                  const filesToAdd = validFiles.filter(file => {
                    const fileWasNotUploadedAlready = !selectedFiles || !selectedFiles.find(f => f.fileName == file.fileName);
                    return fileWasNotUploadedAlready;
                  });
                  const updatedFiles = selectedFiles ? selectedFiles.concat(...filesToAdd) : filesToAdd;
                  setSelectedFiles(updatedFiles);
                  handleLocalError(updatedFiles);
                }} />
              </>
            )}
          </li>
        </FilesList>
        <DataFormat>Gültige Dateiformate: pdf, doc, jpg, txt</DataFormat>
        {
          localError && <ErrorText>{errorMessage}</ErrorText>
        }
      </InputInnerWrapper>
    </InputWrapper>
  );
};
